import { useCallback } from 'react';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';

import { useSession } from '@/hooks/useSession';

/**
 * Converts a camelCase flag key (e.g. `showTutorialDashboard`) into the
 * kebab-case attribute name expected by the Rails user-detail payload
 * (`show-tutorial-dashboard`). Mirrors the legacy regex behavior.
 *
 * @param {string} flagKey
 * @returns {string}
 */
function toKebabCase(flagKey) {
  return flagKey.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Encapsulates the API call that marks a given onboarding tutorial as "seen".
 *
 * Mirrors the mutation performed by the legacy `onboarding-legacy/index.js`,
 * persisting the flag inside `user-detail-attributes` and refreshing the
 * session cache on success.
 *
 * @param {string | undefined} flagKey
 * @returns {{ markAsSeen: () => Promise<void> }}
 */
export function useOnboardingPersistence(flagKey) {
  const client = useClient();
  const { session, sanitizeResponse, setSession } = useSession();

  const markAsSeen = useCallback(async () => {
    const studentId = session?.user?.id;
    const userDetailId = session?.user?.userDetail?.id;

    if (!flagKey || !studentId || !userDetailId) {
      return;
    }

    const attributeName = toKebabCase(flagKey);

    const { error, data } = await client.mutate(`/users/${studentId}`, {
      'user-detail-attributes': {
        id: userDetailId,
        [attributeName]: false
      }
    });

    if (error) {
      toast.error('Erro');
      return;
    }

    if (typeof sanitizeResponse === 'function' && typeof setSession === 'function') {
      const newUser = sanitizeResponse(data);
      setSession({ user: newUser });
    }
  }, [client, flagKey, sanitizeResponse, session, setSession]);

  return { markAsSeen };
}
